import { Card } from "@/components/Card";
import { H2 } from "@/components/Typography";
import {
  getMonthlyBreakdownTableRowData,
  getTopLevelCardsStats,
} from "@/utils/emailDashboardMetricsHelper";
import { getCurrencySymbol } from "@/utils/helpers";
import { useTranslation } from "react-i18next";

interface IProps {
  data: IGetEmailDashboardRS;
}

const MonthlyBreakdownTable = ({ data }: IProps) => {
  const { t } = useTranslation();

  const renderMonthlyRows = () =>
    data?.rawData?.map((row, index) => {
      const {
        month,
        verifiedContacts,
        marketingConsentRate,
        subscribedContacts,
        incrementalSubscribers,
        repeatPurchases,
        repeatPurchaseRevenue,
      } = getMonthlyBreakdownTableRowData(row);

      return (
        <tr
          key={index}
          className="border-b border-gray-light hover:bg-primary-light/50">
          <td className="p-10 text-left">{month}</td>
          <td className="p-10 text-center">{verifiedContacts}</td>
          <td className="p-10 text-center">{marketingConsentRate}%</td>
          <td className="p-10 text-center">{subscribedContacts}</td>
          <td className="p-10 text-center">{incrementalSubscribers}</td>
          <td className="p-10 text-center">{repeatPurchases}</td>
          <td className="p-10 text-center font-bold text-green">
            {getCurrencySymbol(data.shopCurrency)}
            {repeatPurchaseRevenue}
          </td>
        </tr>
      );
    });

  const renderTableFooter = () => {
    const {
      verifiedContacts,
      marketingConsentRate,
      subscribedContacts,
      incrementalSubscribers,
      repeatPurchases,
      repeatPurchaseRevenue,
    } = getTopLevelCardsStats(data.rawData);

    return (
      <tfoot>
        <tr className="bg-green-light/30 font-bold">
          <td className="p-10 text-left">
            {t("email.dashboard.monthlySummaryTable.columns.total")}
          </td>
          <td className="p-10 text-center">{verifiedContacts}</td>
          <td className="p-10 text-center">{marketingConsentRate}%</td>
          <td className="p-10 text-center">{subscribedContacts}</td>
          <td className="p-10 text-center">{incrementalSubscribers}</td>
          <td className="p-10 text-center">{repeatPurchases}</td>
          <td className="p-10 text-center text-green">
            {getCurrencySymbol(data.shopCurrency)}
            {repeatPurchaseRevenue}
          </td>
        </tr>
      </tfoot>
    );
  };

  const renderTable = () => (
    <table className="w-full max-w-full">
      <thead className="border-b border-gray-light">
        <tr>
          <th className="rounded-tl bg-primary p-10 text-left text-white">
            {t("email.dashboard.monthlySummaryTable.columns.month")}
          </th>
          <th className="bg-primary p-10 text-white">
            {t("email.dashboard.monthlySummaryTable.columns.verifiedContacts")}
          </th>
          <th className="bg-primary p-10 text-white">
            {t("email.dashboard.monthlySummaryTable.columns.mcr")}
          </th>
          <th className="bg-primary p-10 text-white">
            {t(
              "email.dashboard.monthlySummaryTable.columns.subscribedContacts",
            )}
          </th>
          <th className="bg-primary p-10 text-white">
            {t(
              "email.dashboard.monthlySummaryTable.columns.incrementalSubscribers",
            )}
          </th>
          <th className="bg-primary p-10 text-white">
            {t("email.dashboard.monthlySummaryTable.columns.repeatPurchases")}
          </th>
          <th className="rounded-tr bg-primary p-10 text-white">
            {t(
              "email.dashboard.monthlySummaryTable.columns.repeatPurchaseRevenue",
            )}
          </th>
        </tr>
      </thead>
      <tbody>{renderMonthlyRows()}</tbody>
      {renderTableFooter()}
    </table>
  );

  /**
   * If for any reason a month includes a `0` value for `verifiedContacts`, then there
   * was an issue with collection/processing on our side and we shouldn't show the breakdown.
   */
  const hasZeroVerifiedContactsInAMonth = data?.rawData?.find(
    month => month.verifiedContacts === 0,
  );

  if (hasZeroVerifiedContactsInAMonth) return null;

  return (
    <>
      <H2 className="mb-5 font-bold">
        {t("email.dashboard.titles.monthlySummary")}
      </H2>
      <hr className="mb-20" />
      <Card shadow additionalClassName="p-20 overflow-x-scroll">
        {renderTable()}
      </Card>
    </>
  );
};

export default MonthlyBreakdownTable;
